import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { useQueryLog } from './useQueryLog'

export function useTransactions(page = 1, pageSize = 100, search = '', sortBy = 'transaction_date', sortOrder = 'desc', advisorId = null) {
  const { logQuery } = useQueryLog()
  const [transactions, setTransactions] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchTransactions()
  }, [page, search, sortBy, sortOrder, advisorId])

  const fetchTransactions = async () => {
    try {
      setLoading(true)
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      // When filtering by advisor, get policy_numbers for their policies
      let policyNumbers = null
      if (advisorId) {
        logQuery('SELECT', 'policy_advisors')
        const { data: pa } = await supabase
          .from('policy_advisors')
          .select('policies(policy_number)')
          .eq('advisor_id', advisorId)
        policyNumbers = (pa || [])
          .map(r => r.policies?.policy_number)
          .filter(Boolean)
        if (policyNumbers.length === 0) {
          setTransactions([])
          setTotal(0)
          setLoading(false)
          return
        }
      }

      logQuery('SELECT', 'policy_transactions')
      let query = supabase
        .from('policy_transactions')
        .select('*, policy_id', { count: 'exact' })

      if (policyNumbers) {
        query = query.in('policy_invoice_no', policyNumbers)
      }

      if (search) {
        query = query.or(
          `policy_invoice_no.ilike.%${search}%,client.ilike.%${search}%,product.ilike.%${search}%`
        )
      }

      const { data, count, error: err } = await query
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to)

      if (err) throw err
      setTransactions(data || [])
      setTotal(count || 0)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createTransaction = async (data) => {
    const { data: result, error } = await supabase
      .from('policy_transactions')
      .insert([data])
      .select()
    if (error) throw error
    await fetchTransactions()
    return result[0]
  }

  const updateTransaction = async (id, data) => {
    const { data: result, error } = await supabase
      .from('policy_transactions')
      .update(data)
      .eq('id', id)
      .select()
    if (error) throw error
    await fetchTransactions()
    return result[0]
  }

  const toggleActive = async (id, currentValue) => {
    setTransactions(prev => prev.map(t => t.id === id ? { ...t, active: !currentValue } : t))
    const { error } = await supabase
      .from('policy_transactions')
      .update({ active: !currentValue })
      .eq('id', id)
    if (error) {
      setTransactions(prev => prev.map(t => t.id === id ? { ...t, active: currentValue } : t))
    }
  }

  // Returns array of invoice numbers that already exist in DB (kept for manual use)
  const checkDuplicates = async (invoiceNos) => {
    if (!invoiceNos || invoiceNos.length === 0) return []
    const { data } = await supabase
      .from('policy_transactions')
      .select('policy_invoice_no')
      .in('policy_invoice_no', invoiceNos)
    return (data || []).map(r => r.policy_invoice_no)
  }

  // Check if a batch with same date + count + advisor total already exists
  const checkDuplicateBatch = async (importDate, count, totalAdvisorExcl) => {
    const { data } = await supabase
      .from('import_batches')
      .select('id, imported_at, transaction_count')
      .eq('import_date', importDate)
      .eq('transaction_count', count)
      .eq('total_advisor_excl', totalAdvisorExcl)
      .maybeSingle()
    return data || null
  }

  // Fetch all import batches (newest first)
  const fetchBatches = async () => {
    logQuery('SELECT', 'import_batches')
    const { data, error: err } = await supabase
      .from('import_batches')
      .select('*')
      .order('imported_at', { ascending: false })
    if (err) throw err
    return data || []
  }

  // Import rows — creates a batch record and attaches batch_id to all rows
  // TX Date defaults to today (import date) if not present in Excel
  const importTransactions = async (rows) => {
    if (rows.length === 0) return { inserted: 0, batchId: null }

    const today = new Date().toISOString().split('T')[0]

    // Calculate batch totals
    const totalSupplierExcl = rows.reduce((s, r) => s + (r.supplier_amount_excl || 0), 0)
    const totalAdvisorExcl = rows.reduce((s, r) => s + (r.advisor_share_excl || 0), 0)
    const totalAdvisorInc = rows.reduce((s, r) => s + (r.advisor_share_inc || 0), 0)

    // Get next sequential import number
    const { data: lastBatch } = await supabase
      .from('import_batches')
      .select('sequence_number')
      .order('sequence_number', { ascending: false })
      .limit(1)
      .maybeSingle()
    const sequenceNumber = (lastBatch?.sequence_number ?? 0) + 1

    // Create batch record
    logQuery('INSERT', 'import_batches')
    const { data: batch, error: batchErr } = await supabase
      .from('import_batches')
      .insert({
        import_date: today,
        sequence_number: sequenceNumber,
        transaction_count: rows.length,
        total_supplier_excl: Math.round(totalSupplierExcl * 100) / 100,
        total_advisor_excl: Math.round(totalAdvisorExcl * 100) / 100,
        total_advisor_inc: Math.round(totalAdvisorInc * 100) / 100,
      })
      .select()
      .single()
    if (batchErr) throw batchErr

    const batchId = batch.id

    // Fetch all policies to build lookup map (policy_number → policy_id)
    logQuery('SELECT', 'policies')
    const { data: policies } = await supabase.from('policies').select('id, policy_number')
    const policyMap = {}
    if (policies) {
      policies.forEach(p => {
        if (p.policy_number) {
          policyMap[String(p.policy_number).trim().toUpperCase()] = p.id
        }
      })
    }

    // Enrich rows: attach batch_id, default TX date to today, lookup policy_id
    const enrichedRows = rows.map(r => {
      const row = { ...r }
      row.batch_id = batchId
      row.import_date = today
      row.import_sequence = sequenceNumber
      // Default TX date to import date if not present
      if (!row.transaction_date) row.transaction_date = today
      // Look up policy_id: try policy_number column first, then fall back to policy_invoice_no
      if (r.policy_number) {
        const normalizedPolNum = String(r.policy_number).trim().toUpperCase()
        row.policy_id = policyMap[normalizedPolNum] || null
      }
      if (!row.policy_id && r.policy_invoice_no) {
        const normalizedInvoice = String(r.policy_invoice_no).trim().toUpperCase()
        row.policy_id = policyMap[normalizedInvoice] || null
      }
      delete row.policy_number
      return row
    })

    // Chunk into batches of 500
    const chunks = []
    for (let i = 0; i < enrichedRows.length; i += 500) {
      chunks.push(enrichedRows.slice(i, i + 500))
    }
    for (const chunk of chunks) {
      logQuery('INSERT', 'policy_transactions')
      const { error } = await supabase.from('policy_transactions').insert(chunk)
      if (error) throw error
    }

    // Refetch in background — don't let a display error fail the import
    fetchTransactions().catch(() => {})
    return { inserted: enrichedRows.length, batchId }
  }

  // Hard-delete all transactions in selected batches, then delete batch records
  const deleteBatches = async (batchIds) => {
    if (!batchIds || batchIds.length === 0) return
    // Hard-delete transactions
    logQuery('DELETE', 'policy_transactions')
    const { error: txErr } = await supabase
      .from('policy_transactions')
      .delete()
      .in('batch_id', batchIds)
    if (txErr) throw txErr
    // Delete batch records
    logQuery('DELETE', 'import_batches')
    const { error: batchErr } = await supabase
      .from('import_batches')
      .delete()
      .in('id', batchIds)
    if (batchErr) throw batchErr
    await fetchTransactions()
  }

  // Hard-delete ALL transactions and batch records (allows clean reimport)
  const clearAllTransactions = async () => {
    logQuery('DELETE', 'policy_transactions')
    const { error: txErr } = await supabase
      .from('policy_transactions')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000') // delete all
    if (txErr) throw txErr
    logQuery('DELETE', 'import_batches')
    const { error: batchErr } = await supabase
      .from('import_batches')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000')
    if (batchErr) throw batchErr
    await fetchTransactions()
  }

  const deleteTransactions = async (ids) => {
    const { error } = await supabase
      .from('policy_transactions')
      .delete()
      .in('id', ids)
    if (error) throw error
    await fetchTransactions()
  }

  const fetchInactiveCount = async () => {
    const { count, error } = await supabase
      .from('policy_transactions')
      .select('id', { count: 'exact', head: true })
      .eq('active', false)
    if (error) throw error
    return count || 0
  }

  const restoreInactive = async () => {
    const { data, error } = await supabase
      .from('policy_transactions')
      .update({ active: true })
      .eq('active', false)
      .select('id', { count: 'exact' })
    if (error) throw error
    await fetchTransactions()
    return data?.length || 0
  }

  // Re-link all unlinked transactions to their policies by matching policy_invoice_no → policy_number.
  // Returns { linked, stillUnlinked } counts.
  const relinkUnlinked = async () => {
    // Fetch all policies
    logQuery('SELECT', 'policies')
    const { data: policies, error: polErr } = await supabase
      .from('policies')
      .select('id, policy_number')
    if (polErr) throw polErr

    const policyMap = {}
    ;(policies || []).forEach(p => {
      if (p.policy_number) {
        policyMap[String(p.policy_number).trim().toUpperCase()] = p.id
      }
    })

    // Fetch all unlinked transactions
    logQuery('SELECT', 'policy_transactions')
    const { data: unlinked, error: txErr } = await supabase
      .from('policy_transactions')
      .select('id, policy_invoice_no')
      .is('policy_id', null)
    if (txErr) throw txErr

    const rows = unlinked || []
    const toUpdate = rows
      .map(t => ({ id: t.id, policy_id: policyMap[String(t.policy_invoice_no || '').trim().toUpperCase()] || null }))
      .filter(t => t.policy_id !== null)

    // Chunk updates into batches of 200
    for (let i = 0; i < toUpdate.length; i += 200) {
      const chunk = toUpdate.slice(i, i + 200)
      logQuery('UPDATE', 'policy_transactions')
      const { error } = await supabase
        .from('policy_transactions')
        .upsert(chunk, { onConflict: 'id' })
      if (error) throw error
    }

    await fetchTransactions()
    return { linked: toUpdate.length, stillUnlinked: rows.length - toUpdate.length }
  }

  return {
    transactions, total, loading, error,
    createTransaction, updateTransaction, toggleActive,
    checkDuplicates, checkDuplicateBatch, fetchBatches,
    importTransactions, deleteBatches, deleteTransactions,
    clearAllTransactions, fetchInactiveCount, restoreInactive,
    relinkUnlinked,
    refetch: fetchTransactions
  }
}
